import { ChangeDetectorRef, Component, forwardRef, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { DomSanitizer, SafeHtml }                                       from '@angular/platform-browser';
import { generateQuickGuid, SelectionTargetEnum, SelectionTargetResult } from '@cs/core/generate';
import { DashboardHtml }                                               from '../../models/dashboard-html';
import { DashboardPanelComponentBase }                                 from '@cs/components/shared';
import { ArrayUtils }                                                  from '@cs/core';
import { DashboardEventHub }                                           from '../../dashboard-event-hub.service';
import { UntilDestroy, untilDestroyed }                                from '@ngneat/until-destroy';
import { filter }                                                      from 'rxjs/operators';

@UntilDestroy()
@Component({
			   standalone: false,
			   selector:    'cs-dashboard-html',
			   templateUrl: './dashboard-html.component.html',
			   // None: the rule must reach server-rendered [innerHTML] rows (which emulated
			   // encapsulation can't tag). Scoped by the .ipa-clickable class, so it only
			   // affects rows that explicitly opt in.
			   encapsulation: ViewEncapsulation.None,
			   styles:        [`
				   .ipa-clickable { cursor: pointer; }
				   .ipa-clickable:hover > td { background-color: rgba(66, 133, 244, 0.06); }

				   .cs-dashboard-html-host { position: relative; }

				   .statistics-html-container {
					   transition: opacity 0.3s ease, filter 0.3s ease;
				   }
				   .statistics-html-container.is-loading {
					   opacity: 0.55;
					   filter: blur(0.5px);
					   pointer-events: none;
				   }

				   .cs-dashboard-html-loading-overlay {
					   position: absolute;
					   inset: 0;
					   display: flex;
					   align-items: center;
					   justify-content: center;
					   z-index: 5;
					   background-color: rgba(255, 255, 255, 0.55);
					   opacity: 0;
					   pointer-events: none;
					   transition: opacity 0.3s ease;
				   }
				   .cs-dashboard-html-loading-overlay.is-active {
					   opacity: 1;
				   }
				   .cs-dashboard-html-loading-overlay .loader {
					   width: 56px;
					   height: 56px;
					   border-radius: 50%;
					   border: 5px solid rgba(66, 133, 244, 0.18);
					   border-top-color: #4285f4;
					   box-shadow: 0 6px 18px rgba(66, 133, 244, 0.18);
					   animation: cs-dashboard-html-spin 0.7s linear infinite;
				   }
				   @keyframes cs-dashboard-html-spin {
					   to { transform: rotate(360deg); }
				   }
			   `]
		   })
export class DashboardHtmlComponent extends DashboardPanelComponentBase<DashboardHtml> implements OnInit {
	name: string;
	id = generateQuickGuid();

	safeHtml: SafeHtml;
	isLoading        = false;
	/** True only when THIS panel emitted the IPA click that triggered the reload —
	 *  used so only the originating panel shows the spinner; siblings just dim.   */
	isClickOrigin    = false;

	set data(value: DashboardHtml) {
		this._data = value;
		this.setData();
	}

	constructor(private sanitizer: DomSanitizer,
				private cdRef: ChangeDetectorRef,
				@Inject(forwardRef(() => DashboardEventHub)) private dashboardEventHub: DashboardEventHub) {
		super();
	}

	ngOnInit(): void {
		// Generic panel loading: dim the html panel + show a spinner whenever the
		// dashboard broadcasts an `IsLoading` action targeting this panel (or all).
		// Mirrors dashboard-generic-table's loading wiring.
		this.dashboardEventHub.getActionsHook<any, any>()
			.pipe(
				untilDestroyed(this),
				filter(value => value && (value.panelName === this.name || value.panelName === 'broadcast'))
			)
			.subscribe(value => {
				if (!value || value.action !== 'IsLoading')
					return;
				this.setLoading(value.data as boolean);
			});
	}

	private setLoading(loading: boolean): void {
		this.isLoading = loading;
		if (!loading)
			this.isClickOrigin = false;
		// markForCheck walks up and marks ancestor views dirty. Safe to call inside
		// an ongoing CD pass (unlike detectChanges, which throws). Needed because
		// some ancestor in the dashboard tree is OnPush and was eating the update.
		this.cdRef.markForCheck();
	}

	/**
	 * Generic Inter Panel Activity for html panels: a clicked element carrying
	 * data-ipa-* attributes fires the same selection event a cs-table-nxt row does,
	 * so the existing dashboard pipeline updates the filter bar.
	 *
	 *   Simple (single key):
	 *     data-ipa-target : the filter param to set (e.g. "IdCustomer")
	 *     data-ipa-value  : the value to set it to
	 *
	 *   Complex (multi-key / nested, e.g. the location tree):
	 *     data-ipa-selection : JSON string of the full selectionObject, e.g.
	 *                          {"location":{"IdOffice":[1234]}}
	 *
	 *   Optional:
	 *     data-ipa-action : SelectionTargetEnum (default CurrentWindow)
	 */
	onHtmlClick($event: MouseEvent): void {
		const el = ($event.target as HTMLElement)?.closest('[data-ipa-selection], [data-ipa-value]') as HTMLElement;
		if (!el)
			return;

		let selectionObject: { [key: string]: any } | null = null;

		const selectionAttr = el.getAttribute('data-ipa-selection');
		if (selectionAttr) {
			try {
				const parsed = JSON.parse(selectionAttr);
				if (parsed && typeof parsed === 'object')
					selectionObject = parsed;
			} catch (e) {
				// invalid JSON — ignore and try target/value below
			}
		}

		if (selectionObject === null) {
			const target   = el.getAttribute('data-ipa-target');
			const rawValue = el.getAttribute('data-ipa-value');
			if (!target || rawValue === null)
				return;
			const value: string | number = rawValue !== '' && !isNaN(Number(rawValue)) ? Number(rawValue) : rawValue;
			selectionObject = {[target]: value};
		}

		const action = el.getAttribute('data-ipa-action') || SelectionTargetEnum.CurrentWindow;

		const result = {
			selectionObject:  selectionObject,
			selectionAction:  action as SelectionTargetEnum,
			panelName:        this.name,
			selectionContext: {},
			selectionRoute:   null,
			selectionMeta:    {}
		} as SelectionTargetResult;

		// Flag this panel as the click origin so only it shows the spinner during the
		// upcoming reload (sibling panels still receive the loading broadcast → just dim).
		this.isClickOrigin = true;
		this.dashboardEventHub.isDashboardEntryIsClicked.next(result);
	}

	setData(): void {
		this.safeHtml = this.sanitizer.bypassSecurityTrustHtml(this._data.html);
		// New data arriving IS the loading-done signal — guarantees the overlay
		// fades even if the broadcast `false` is dropped or arrives out of order.
		if (this.isLoading)
			this.setLoading(false);
	}

	dataChanged(value: DashboardHtml) {

	}

	update(data: DashboardHtml): void {

		if (this._data && ArrayUtils.isEqual(this._data, data)) {
			// Data didn't change, but the reload still completed — clear the dim.
			if (this.isLoading)
				this.setLoading(false);
			return;
		}

		this._data = data;
		this.setData();
	}
}
